import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';

export interface User {
  id: number;
  username: string;
  name: string;
  role: 'admin' | 'staff';
  is_active: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  public currentUser$ = this.currentUserSubject.asObservable();
  public permissions$ = this.permissionsSubject.asObservable();
  private isBrowser: boolean;

  constructor(
    private api: ApiService,
    private router: Router,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this.loadFromStorage();
    }
  }

  login(username: string, password: string): Observable<any> {
    return new Observable(observer => {
      this.api.post('auth/login.php', { username, password }).subscribe({
        next: (response: any) => {
          if (response.success && response.data) {
            if (this.isBrowser) {
              localStorage.setItem('token', response.data.token);
              localStorage.setItem('user', JSON.stringify(response.data.user));
              this.storePermissions(response.data.permissions ?? []);
            }

            this.currentUserSubject.next(response.data.user);
            this.permissionsSubject.next(response.data.permissions ?? []);
            observer.next(response);
            observer.complete();
          } else {
            observer.error(response);
          }
        },
        error: (error) => observer.error(error)
      });
    });
  }

  logout(): void {
    if (this.isBrowser) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('permissions');
    }
    this.currentUserSubject.next(null);
    this.permissionsSubject.next([]);
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    if (!this.isBrowser) {
      return false;
    }
    return !!localStorage.getItem('token');
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  getPermissions(): string[] {
    return [...this.permissionsSubject.value];
  }

  setPermissions(permissions: string[]): void {
    this.storePermissions(permissions);
    this.permissionsSubject.next([...permissions]);
  }

  checkSession(): Observable<any> {
    return this.api.get('auth/check_session.php');
  }

  refreshSession(): Observable<any> {
    return new Observable((observer) => {
      this.checkSession().subscribe({
        next: (response: any) => {
          if (response.success && response.data?.user) {
            if (this.isBrowser) {
              localStorage.setItem('user', JSON.stringify(response.data.user));
            }
            this.currentUserSubject.next(response.data.user);
            if (Array.isArray(response.data.permissions)) {
              this.setPermissions(response.data.permissions);
            }
          }
          observer.next(response);
          observer.complete();
        },
        error: (error) => observer.error(error),
      });
    });
  }

  hasRole(role: User['role']): boolean {
    return this.getCurrentUser()?.role === role;
  }

  private storePermissions(permissions: string[]): void {
    if (!this.isBrowser) {
      return;
    }
    localStorage.setItem('permissions', JSON.stringify(permissions));
  }

  private loadFromStorage(): void {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        this.currentUserSubject.next(JSON.parse(userStr));
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('permissions');
      }
    }

    const permStr = localStorage.getItem('permissions');
    if (permStr) {
      try {
        const permissions = JSON.parse(permStr);
        if (Array.isArray(permissions)) {
          this.permissionsSubject.next(permissions);
        }
      } catch {
        localStorage.removeItem('permissions');
      }
    }
  }
}
